// @givmo/board — the account's own view as a director: `GET /board-director/organizations/me`.
//
// One read answers three things every director page needs (contract §6): which boards this account
// holds a claimed seat on, across organizations and both classes; this sign-in's signing window
// (`signing`, computed server-side so law 8 holds); and the words each act affirms beside its button
// (`intent_statements`, whose ids the acts echo).
//
// ⚠ ONE CACHE ENTRY, `boardDirectorMeKey()`. The shell, the chip, the act rows, the read gate and the
// pages all read it, and a confirmation, a sign-in or a sign-out invalidates it, so every consumer
// re-derives at once. A hand-typed twin of the key that drifted would fork a second entry. The key
// names no board: the answer is the account's, and an account may sit on several.
//
// Typed from the generated model (`OrgBoardDirectorMeOut`, through `director-wire.ts`'s decoded
// shapes) and decoded tolerantly off `unknown`: a malformed answer reads as no boards and a shut
// window, never a thrown render.

import {
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useBoardDirectorOperations } from '../board-runtime';
import { useBoardDirectorHost } from './director-host';
import { isDirector } from '../features/roster/officer-roles';
import {
  isCommitteeOnly,
  type BoardSigningAct,
  type DirectorBoard,
  type DirectorIntentStatement,
  type DirectorMe,
  type DirectorSigning,
  type ListedSeat,
} from './director-wire';

/** The ONE key for `/me`. */
export function boardDirectorMeKey() {
  return ['board-director-me'] as const;
}

const SHUT: DirectorSigning = {
  open: false,
  method: null,
  confirmed_at: null,
  closes_at: null,
  closed_reason: null,
  account_has_passkey: null,
  cool_off_ends_at: null,
};

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** One directory entry — `/me`'s, or the claim's answer (the seat as `/me` lists it). */
export function decodeDirectorBoard(value: unknown): DirectorBoard | null {
  const row = record(value);
  const orgRef = text(row?.org_ref);
  const seat = text(row?.board_member_id);
  if (row === null || orgRef === null || seat === null) return null;
  return {
    board_member_id: seat,
    org_ref: orgRef,
    organization_name: text(row.organization_name),
    display_name: text(row.display_name) ?? '',
    email_of_record: text(row.email_of_record) ?? '',
    board_role: text(row.board_role) ?? '',
    // CBG-YR3-ROLES layer 1: the person's two facts on this board. Director status is read through
    // the one predicate (`officer-roles.ts`): an answer from before the fact reads the legacy word's
    // voting set as directors. Such an answer has no titles.
    is_director: isDirector({ is_director: row.is_director, board_role: text(row.board_role) }),
    title_labels: Array.isArray(row.title_labels)
      ? row.title_labels
          .filter((label): label is string => typeof label === 'string' && label.trim().length > 0)
          .map((label) => label.trim())
      : [],
    // CBG-YR3-ROLES layer 2: the same titles with their ids — what this person's own minutes
    // signature may name as its capacity ("Signing as"). An answer without them offers none.
    titles: Array.isArray(row.titles)
      ? row.titles
          .map((title) => {
            const entry = record(title);
            const titleId = text(entry?.title_id);
            const label = text(entry?.label);
            return titleId === null || label === null || label.trim().length === 0
              ? null
              : { title_id: titleId, label: label.trim() };
          })
          .filter((title): title is { title_id: string; label: string } => title !== null)
      : [],
    status: text(row.status) ?? '',
    term_start_on: text(row.term_start_on),
    term_end_on: text(row.term_end_on),
    // `access_mode` and `step_up_required_for_all_access` are retired with the board-adopted
    // "confirm before anything opens" (D-CBG-20) and are not read.
    // CBG-SEC2-L4: Board's verdict for this account on this board, absent when it isn't known.
    another_account_required:
      typeof row.another_account_required === 'boolean' ? row.another_account_required : null,
    // CBG-YR6-OUTSIDE: a seat for someone who isn't on the board. Any other value, or none, is the
    // board's: the narrower reading is never assumed.
    scope: text(row.scope) === 'committees' ? 'committees' : 'board',
    // CBG-YR6-MEET (frame c8): the committees this person serves on here, as the registry names
    // them; an answer from before them lists none.
    committees: Array.isArray(row.committees)
      ? row.committees
          .map((committee) => {
            const entry = record(committee);
            const committeeId = text(entry?.committee_id);
            const name = text(entry?.name);
            return committeeId === null || name === null
              ? null
              : { committee_id: committeeId, name, role: text(entry?.role) ?? 'member' };
          })
          .filter(
            (committee): committee is { committee_id: string; name: string; role: string } =>
              committee !== null,
          )
      : [],
  };
}

function decodeSigning(value: unknown): DirectorSigning {
  const row = record(value);
  if (row === null) return SHUT;
  const passkey = row.account_has_passkey;
  return {
    open: row.open === true,
    method: text(row.method),
    confirmed_at: text(row.confirmed_at),
    closes_at: text(row.closes_at),
    closed_reason: text(row.closed_reason),
    account_has_passkey: typeof passkey === 'boolean' ? passkey : null,
    // CBG-FIX127: when the pause after a sign-in method's removal ends, served only while it is
    // ahead; an answer without it (an older backend, or no pause) reads as none.
    cool_off_ends_at: text(row.cool_off_ends_at),
  };
}

function decodeIntent(value: unknown): DirectorIntentStatement | null {
  const row = record(value);
  const act = text(row?.act);
  const id = text(row?.id);
  const words = text(row?.text);
  return act === null || id === null || words === null ? null : { act, id, text: words };
}

/** `/me`'s answer, read tolerantly: never throws, and an unreadable field reads as absent or shut. */
export function decodeDirectorMe(data: unknown): DirectorMe {
  const row = record(data);
  const boards = Array.isArray(row?.boards) ? row.boards : [];
  const intents = Array.isArray(row?.intent_statements) ? row.intent_statements : [];
  return {
    boards: boards
      .map(decodeDirectorBoard)
      .filter((board): board is DirectorBoard => board !== null),
    signing: decodeSigning(row?.signing),
    intent_statements: intents
      .map(decodeIntent)
      .filter((intent): intent is DirectorIntentStatement => intent !== null),
  };
}

// ── The boards `/me` has listed since the page loaded (CBG-FIX34) ──────────────────────────────────
//
// A board page whose `/me` stops listing its board — the board refused this account ("You no longer
// have access to this board.") and the re-read no longer lists it — says the refusal's own sentence,
// since the page held the seat a moment ago. A page reached by an address naming a board this account
// never had a seat on keeps "You don't have a seat on this board with this account." The difference
// is remembered per query client, so it lasts until a reload: after one, nothing shows the seat was
// ever this account's, and the page says the original line.
//
// CBG-FIX104 (row 4.1): each board is remembered with the seat `/me` last listed there — whether it
// was a committee's, for someone who isn't on the board, and on how many committees — so a refusal
// met after the seat is gone says "this committee" (or "these committees") where a board seat's
// says "this board". The latest listing wins: a seat added to the board reads as the board's.
const LISTED = new WeakMap<QueryClient, Map<string, ListedSeat>>();

function rememberListed(client: QueryClient, me: DirectorMe): void {
  const listed = LISTED.get(client) ?? new Map<string, ListedSeat>();
  for (const board of me.boards) {
    listed.set(board.org_ref, {
      committeeOnly: isCommitteeOnly(board),
      committees: (board.committees ?? []).length,
      names: (board.committees ?? []).map((row) => row.name).filter((name) => name.length > 0),
    });
  }
  LISTED.set(client, listed);
}

/** Whether `/me` listed this board earlier since the page loaded: this account held a seat on it. */
export function useListedBefore(orgRef: string | null): boolean {
  return useListedSeat(orgRef) !== null;
}

/** The seat `/me` last listed on this board since the page loaded, or null when it never did. */
export function useListedSeat(orgRef: string | null): ListedSeat | null {
  const client = useQueryClient();
  return orgRef === null ? null : (LISTED.get(client)?.get(orgRef) ?? null);
}

/** The account's `/me`. It fires only for a signed-in account: a signed-out page has no `/me`. */
export function useDirectorMe(): UseQueryResult<DirectorMe> {
  const operations = useBoardDirectorOperations();
  const { account } = useBoardDirectorHost();
  const client = useQueryClient();
  return useQuery({
    queryKey: boardDirectorMeKey(),
    enabled: account !== null,
    queryFn: async () => {
      const { data } = await operations.directorGetBoardDirectorMe({ throwOnError: true });
      const me = decodeDirectorMe(data);
      rememberListed(client, me);
      return me;
    },
  });
}

/** The board `org_ref` names in this account's directory, or null when it holds no seat there. */
export function directorBoardFor(
  me: DirectorMe | undefined,
  orgRef: string | null,
): DirectorBoard | null {
  if (me === undefined || orgRef === null) return null;
  return me.boards.find((board) => board.org_ref === orgRef) ?? null;
}

/** The words one act affirms, or null until `/me` says them: the page never invents them. */
export function intentStatementFor(
  me: DirectorMe | undefined,
  act: BoardSigningAct,
): DirectorIntentStatement | null {
  return me?.intent_statements.find((intent) => intent.act === act) ?? null;
}
